import {
  IAddUpdateOrdersState,
  ICheckProductRequestPayload,
  ICreateOrderPayload,
  IFetchOrderDetailResponse,
  IFetchOrderResponse,
  IFetchProductsForOrderPayload,
  IOdooTaxListResponse,
  IOrderAddUpdateList,
  IOrderDetailResponseNormalized,
  IUpdateOrderPayload,
  IOrderDiscountObject,
  IZoneInfoState,
  IPartnerChannel,
  ITaxBreakDownDetails,
} from "../interfaces/Index";
import {
  convertDateFormat,
  formatDate,
  formatToCurrency,
  orderListChannel,
} from "../utils/lib";

export const normalizeCreateOrderPayload = (
  payload: IAddUpdateOrdersState,
  taxList: IOdooTaxListResponse[],
  orderLevelDiscountManualAndPromo: IOrderDiscountObject,
  zoneInfo: IZoneInfoState,
  selectPlan: string | null,
  grandTotal: any | null
) => {
  let orderPayload: ICreateOrderPayload = {
    customer: {
      id: payload.customerId.toString() as string,
      phone: payload.customerNumber,
      shippingAddress: payload.customerAddress || "",
      storeName: payload.customerStore,
      zoneInfo: zoneInfo,
      customerChannel: payload.customerChannel,
    },
    partnerChannels: payload.partnerChannels || [],
    items: payload.orderList.map((item) => {
      const taxRecord = taxList.find((tax) => tax.id === item.salesTaxId);
      const itemPayload: any = {
        id: item.id.toString(),
        discount: {
          type: "VALUE",
          value: parseFloat(item.additionalDiscount as any),
        },
        type: "PRODUCT",
        name: item.itemName,
        quantity: item.quantity,
        unitPrice: item.itemPrice,
        variantId: item.variantId,
        stock: item.stock,
        originalPrice: item.productOriginalPrice || 0,
        uomSale: item.uomSale || "",
        categoryId: item.categoryId || "",
        brandId: item.brandId || "",
      };
      if (taxRecord) {
        itemPayload.tax = {
          id: (taxRecord as IOdooTaxListResponse).id,
          amount: (taxRecord as IOdooTaxListResponse).amount,
          amountType: (taxRecord as IOdooTaxListResponse).amountType,
          taxType: (taxRecord as IOdooTaxListResponse).taxType,
        };
      }
      return itemPayload;
    }),
  };

  if (
    !orderLevelDiscountManualAndPromo.isBasketDiscount &&
    payload.orderDiscount &&
    payload.orderDiscount > 0
  ) {
    orderPayload.flatDiscount = {
      type: "VALUE",
      value: parseFloat(payload.orderDiscount as any),
    };
  }
  if (
    orderLevelDiscountManualAndPromo.isBasketDiscount &&
    Number(orderLevelDiscountManualAndPromo.value) > 0
  ) {
    orderPayload.promos = [
      {
        promoId: orderLevelDiscountManualAndPromo.promoId,
        type: orderLevelDiscountManualAndPromo.promoType,
        value: Number(orderLevelDiscountManualAndPromo.value),
      },
    ];
  }
  if (selectPlan && grandTotal) {
    orderPayload.creditAmount = Number(grandTotal);
    orderPayload.planId = selectPlan;
  }

  return orderPayload;
};

export const normalizeUpdateOrderPayload = (
  payload: Array<IOrderAddUpdateList>,
  orderDiscount: number | string,
  taxList: IOdooTaxListResponse[],
  orderLevelDiscountManualAndPromo: IOrderDiscountObject,
  deliveryDate: string | null,
  orderType: string,
  partnerChannels: IPartnerChannel[]
) => {
  let normalizedPayload: IUpdateOrderPayload = {
    items: payload.map((item) => {
      const taxRecord = taxList.find((tax) => tax.id === item.salesTaxId);
      const itemPayload: any = {
        id: item.id.toString(),
        discount: {
          type: "VALUE",
          value: parseFloat(item.additionalDiscount as any),
        },
        type: "PRODUCT",
        name: item.itemName,
        quantity: item.quantity,
        unitPrice: item.itemPrice,
        variantId: item.variantId,
        stock: item.stock,
        originalPrice: item.productOriginalPrice || 0,
        uomSale: item.uomSale || "",
        categoryId: item.categoryId || "",
        brandId: item.brandId || "",
      };
      if (taxRecord) {
        itemPayload.tax = {
          id: (taxRecord as IOdooTaxListResponse).id,
          amount: (taxRecord as IOdooTaxListResponse).amount,
          amountType: (taxRecord as IOdooTaxListResponse).amountType,
          taxType: (taxRecord as IOdooTaxListResponse).taxType,
        };
      }
      return itemPayload;
    }),
    partnerChannels: partnerChannels,
  };

  if (
    !orderLevelDiscountManualAndPromo.isBasketDiscount &&
    orderDiscount &&
    (orderDiscount as any) > 0
  ) {
    normalizedPayload.flatDiscount = {
      type: "VALUE",
      value: parseFloat(orderDiscount as any),
    };
  }

  if (
    orderLevelDiscountManualAndPromo.isBasketDiscount &&
    Number(orderLevelDiscountManualAndPromo.value) > 0
  ) {
    normalizedPayload.promos = [
      {
        promoId: orderLevelDiscountManualAndPromo.promoId,
        type: orderLevelDiscountManualAndPromo.promoType,
        value: Number(orderLevelDiscountManualAndPromo.value),
      },
    ];
  }
  if (deliveryDate) {
    // convert date to UTC format so that Date.parse doesnt
    // convert it into UTC before parsing
    normalizedPayload.deliveryDate = Date.parse(deliveryDate);
  }

  if (orderType == "DO") {
    normalizedPayload.deliveryType = "DROPSHIP";
    normalizedPayload.vehicleProvider = "DROPSHIP_SUPPLIER";
  }

  return normalizedPayload;
};

export const normalizeFetchOrderResponse = (
  res: Array<IFetchOrderResponse>,
  pagination: number | boolean,
  isPaginatedRes: boolean
) => {
  let payload: any = {
    pagination: res.length === 25 ? pagination : false,
    isPaginatedRes,
  };
  payload.ordersData = res.map((order: IFetchOrderResponse) => {
    return {
      id: order.id ? order.id : "",
      orderNumber: order.orderNumber ? order.orderNumber : "",
      orderDate: convertDateFormat(order.date),
      storeName: order.storeName,
      orderTotal: "Rs. " + formatToCurrency(order.total),
      orderChannel: orderListChannel(order.channel),
      orderStatus: order.status,
      isLocked: order.isLocked,
    };
  });
  return payload;
};

export const normalizeOrderDetailsResponse = (
  data: IFetchOrderDetailResponse
) => {
  let normalizeData: IOrderDetailResponseNormalized = {
    customerNTN: "",
    customerStore: data.storeName,
    customerId: data.customerId || "",
    customerNumber: data.phoneNumber,
    customerAddress: data.shippingAddress,
    customerChannel: data.customerChannel,
    orderNumber: data.orderNumber,
    createdDate: formatDate(data.createdDate, "do MMM, h:mm aaa"),
    createdBy: data.createdBy,
    updatedDate: formatDate(data.updatedDate, "do MMM, h:mm aaa"),
    updatedBy: data.updatedBy,
    fulfillmentStatus: data.fulfillmentStatus,
    status: data.status,
    returnStatus: data.returnStatus || "",
    orderDiscount: data.discount || 0,
    zoneId: data.zoneId,
    promos: data.promos,
    walletAmount: data.walletAmount || 0,
    isLocked: data.isLocked,
    deliveryType: data.deliveryType,
    orderList: data.orderItems.map((item) => {
      const taxRecord = data.taxBreakDowns.find((t) => t.itemId === item.id);

      const furtherAndSalesTaxWhenBoth = data.taxBreakDowns.filter(
        (t) => t.itemId === item.id
      );
      let salesTaxRecord = null;
      let furtherTaxRecord = null;

      if (furtherAndSalesTaxWhenBoth.length > 1) {
        salesTaxRecord = furtherAndSalesTaxWhenBoth.find(
          (f) => f.taxSubType == "SALE"
        );
        furtherTaxRecord = furtherAndSalesTaxWhenBoth.find(
          (f) => f.taxSubType == "FURTHER"
        );
      } else {
        salesTaxRecord =
          taxRecord && taxRecord.taxSubType == "SALE" ? taxRecord : null;
        furtherTaxRecord =
          taxRecord && taxRecord.taxSubType == "FURTHER" ? taxRecord : "";
      }

      return {
        id: item.id,
        variantId: item.variantId,
        itemName: item.itemName,
        itemPrice: item.unitPrice - item.discount,
        quantity: item.quantity,
        originalQuantity: item.quantity || null,
        discount: item.discount,
        stock: item.stock,
        additionalDiscount: item.additionalDiscount,
        productOriginalPrice: item.unitPrice,
        uomSale: item.uomSale || "",
        purchaseTaxId: item.purchaseTaxId || "",
        salesTaxId: salesTaxRecord ? salesTaxRecord.taxId : "",
        salesTaxAmount: salesTaxRecord ? salesTaxRecord.amount : 0.0,
        furtherTaxAmount: furtherTaxRecord ? (furtherTaxRecord as ITaxBreakDownDetails).amount : 0.0,
        bundleId: item.bundleId,
      };
    }),
    expectedDeliveryDate: data.expectedDeliveryDate,
  };
  return normalizeData;
};

export const normalizeCheckProductPayload = (
  payload: IAddUpdateOrdersState,
  zoneInfo: IZoneInfoState,
  customerChannel: string
) => {
  let checkProductAvailabilityPayload: ICheckProductRequestPayload = {
    customer: {
      id: payload.customerId.toString(),
    },
    customerChannel: customerChannel,
    partnerChannels: payload.partnerChannels || [],
    zoneInfo: zoneInfo,
    products: payload.orderList.map((item) => {
      return {
        id: item.id.toString(),
        price: item.itemPrice,
        quantity: item.quantity,
      };
    }),
  };
  return checkProductAvailabilityPayload;
};

export const normalizeFetchProductsForOrderPayload = (
  paginationDetails: any,
  zoneInfo: IZoneInfoState,
  customerChannel: string,
  partnerChannels: IPartnerChannel[]
) => {
  let normalizedPayload: IFetchProductsForOrderPayload = {
    paginationDetails: paginationDetails,
    zoneInfo: zoneInfo,
    customerChannel: customerChannel,
    partnerChannels: partnerChannels,
  };

  return normalizedPayload;
};
